import { useEffect, useRef } from "react";
import type { RefObject } from "react";

/**
 * Form-viewport helpers — enforce the "never scroll to complete a form" rule.
 *
 * The site header is fixed, so any programmatic scroll must land the target just
 * BELOW it. The Header writes its live height into `--app-header-h`; we read that
 * here so the offset stays correct across breakpoints, zoom, and orientation.
 *
 * Forms live in two contexts:
 *   1. Page forms  -> the window scrolls.
 *   2. Modal/Sheet -> a scroll container inside the dialog scrolls.
 * The helpers below detect which applies and scroll the right thing.
 */

function headerOffset(): number {
  if (typeof window === "undefined") return 0;
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--app-header-h");
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : 72;
}

/** Nearest scrollable ancestor, or null when the page itself scrolls. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    const oy = getComputedStyle(node).overflowY;
    if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/** Bring `el`'s top just below the sticky header (page) or to the top (modal). */
export function scrollFormTargetIntoView(el: HTMLElement | null) {
  if (!el || typeof window === "undefined") return;
  const container = scrollParent(el);
  if (container) {
    const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 12;
    container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  } else {
    const top = el.getBoundingClientRect().top + window.scrollY - headerOffset() - 12;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }
}

/**
 * Scroll `ref` into view whenever `key` changes (wizard step change, validation
 * error, opening a new form state). Skips the initial mount so pages with a hero
 * above the form open at the top instead of jumping to the form.
 */
export function useScrollIntoViewOn(ref: RefObject<HTMLElement>, key: unknown) {
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const raf = window.requestAnimationFrame(() => scrollFormTargetIntoView(ref.current));
    return () => window.cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

function isFormField(el: Element | null): el is HTMLElement {
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (el as HTMLElement).isContentEditable;
}

/**
 * App-wide safety net (call once at the root): whenever a form control is focused
 * and it would sit under the on-screen keyboard or behind the fixed header, scroll
 * it into the visible area. Covers every form on the site — guest and admin — so a
 * field or its action button is never hidden. Only scrolls when actually needed.
 */
export function useGlobalFormKeyboardHandling() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const ensureVisible = () => {
      const active = document.activeElement;
      if (!isFormField(active)) return;
      const rect = active.getBoundingClientRect();
      const topSafe = headerOffset() + 8;
      const bottomSafe = (window.visualViewport?.height ?? window.innerHeight) - 8;
      if (rect.bottom > bottomSafe || rect.top < topSafe) {
        active.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    };

    const onFocusIn = (e: FocusEvent) => {
      if (isFormField(e.target as Element)) window.setTimeout(ensureVisible, 140);
    };

    document.addEventListener("focusin", onFocusIn);
    window.visualViewport?.addEventListener("resize", ensureVisible);
    window.addEventListener("orientationchange", ensureVisible);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      window.visualViewport?.removeEventListener("resize", ensureVisible);
      window.removeEventListener("orientationchange", ensureVisible);
    };
  }, []);
}

/**
 * Keep the focused field — and the action button under it — visible above the
 * on-screen keyboard. On focus we center the control in whatever space remains,
 * and we re-run it when the visual viewport resizes (keyboard open/close) or the
 * device rotates. Satisfies the mobile-keyboard and orientation rules.
 */
export function useKeyboardAwareForm(ref: RefObject<HTMLElement>) {
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof window === "undefined") return;

    const centerActive = () => {
      const active = document.activeElement as HTMLElement | null;
      if (!active || !root.contains(active)) return;
      const tag = active.tagName;
      if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT" && !active.isContentEditable) return;
      active.scrollIntoView({ block: "center", behavior: "smooth" });
    };

    const onFocusIn = (e: FocusEvent) => {
      if (!root.contains(e.target as Node)) return;
      // Let the keyboard begin animating in before measuring.
      window.setTimeout(centerActive, 140);
    };

    root.addEventListener("focusin", onFocusIn);
    window.visualViewport?.addEventListener("resize", centerActive);
    window.addEventListener("orientationchange", centerActive);
    return () => {
      root.removeEventListener("focusin", onFocusIn);
      window.visualViewport?.removeEventListener("resize", centerActive);
      window.removeEventListener("orientationchange", centerActive);
    };
  }, [ref]);
}
